import { useState, useEffect, useCallback } from "react";
import { offlineDb, type OfflineLibrarySection } from "@/core/offline/db";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ultra-mesure-token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

// ─── Fetch + cache dans Dexie ────────────────────────────────

async function fetchAndCacheLibrary(): Promise<void> {
  const res = await fetch(`/api/library`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const { sections } = await res.json();

  await offlineDb.transaction("rw", offlineDb.librarySections, async () => {
    await offlineDb.librarySections.clear();
    await offlineDb.librarySections.bulkAdd(
      sections.map((s: OfflineLibrarySection) => ({
        ...s,
        createdAt: new Date(s.createdAt),
        updatedAt: new Date(s.updatedAt),
      }))
    );
  });
}

// ─── Hook public ─────────────────────────────────────────────

export interface LibrarySection extends OfflineLibrarySection {}

export function useLibrary() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lecture réactive depuis Dexie (tri en mémoire, "order" n'est pas indexé)
  const sections = useLiveQuery(
    () => offlineDb.librarySections.toArray().then((rows) =>
      rows.sort((a, b) => a.order - b.order)
    ),
    []
  ) ?? [];

  // Charge depuis le serveur au premier montage
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchAndCacheLibrary();
    } catch {
      setError("Impossible de charger la librairie");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ─── Sections groupées par catégorie ──────────────────────
  const byCategory = sections.reduce<Record<string, LibrarySection[]>>((acc, s) => {
    const cat = s.category ?? "custom";
    (acc[cat] ??= []).push(s);
    return acc;
  }, {});

  // ─── CRUD ──────────────────────────────────────────────────

  async function createSection(data: {
    title: string;
    content: unknown;
    category?: string;
  }): Promise<LibrarySection> {
    const res = await fetch(`/api/library`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Erreur ${res.status}`);
    const { section } = await res.json();
    await offlineDb.librarySections.put({
      ...section,
      createdAt: new Date(section.createdAt),
      updatedAt: new Date(section.updatedAt),
    });
    return section;
  }

  async function updateSection(
    id: string,
    data: Partial<Pick<LibrarySection, "title" | "content" | "category" | "order">>
  ): Promise<void> {
    const res = await fetch(`/api/library/${id}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Erreur ${res.status}`);
    const { section } = await res.json();
    await offlineDb.librarySections.put({
      ...section,
      createdAt: new Date(section.createdAt),
      updatedAt: new Date(section.updatedAt),
    });
  }

  async function deleteSection(id: string): Promise<void> {
    const res = await fetch(`/api/library/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(`Erreur ${res.status}`);
    await offlineDb.librarySections.delete(id);
  }

  return {
    sections,
    byCategory,
    loading,
    error,
    refresh,
    createSection,
    updateSection,
    deleteSection,
  };
}
